"use client"

import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, X, UserIcon, Download, Loader2 } from "lucide-react"
import { Calendar } from "../../components/ui/calendar"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Badge } from "../../components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover"

import AddApproachModal from "../../components/AddApproachModal"
import CallbackModal from "../../components/CallbackModal"
import {
  exportApproaches,
  exportCompanyApproaches,
  getApproaches,
  getCompanyApproaches,
  updateApproach,
  updateCompanyApproach,
} from "../../services/approachesapi"
import { getMySubordinates } from "../../services/usersapi"
import { format } from "date-fns"
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select"
import { useNavigate } from "react-router-dom"
import { toast } from "react-hot-toast" 
import { cn } from "../../components/lib/utils"
import { useRbac } from "../../context"
import * as XLSX from "xlsx"
import AddCompanyModal from "../../components/AddCompanyApproch"

const jobPortals = [
  "EarlyJobs",
  "Naukri.com",
  "Indeed India",
  "LinkedIn Jobs",
  "Foundit",
  "Shine.com",
  "TimesJobs",
  "Glassdoor",
  "Google Jobs",
  "Freshersworld",
  "Apna.co",
  "Cutshort",
  "Hirect",
  "Hirist",
  "LetsIntern",
  "JobsForHer",
  "WorkIndia",
  "Wellfound",
  "QuikrJobs",
  "Jobsora",
  "WisdomJobs",
  "Jagran Josh Jobs",
  "CareerIndia",
  "CareerAge",
  "Neuvoo / Talent.com",
  "Recruit.net",
  "WhatJobs.com",
  "JobSquare",
  "SarkariNaukri.com",
  "FreeJobAlert.com",
  "RozgarIndia",
  "JobGranth",
  "NaukriHub",
  "MonsterGulf",
  "CareerJet India",
  "JobSarkari",
  "Times Ascent",
  "Updazz",
  "Instahyre",
  "PlacementIndia",
  "SarkariResult.com",
  "FreshersLive",
  "JobStreet India",
  "PlacementIndia.com",
  "Youth4Work",
  "JobNukkad",
  "TeamLease Jobs",
  "Babajob",
  "Jooble India",
  "ClickIndia Jobs",
  "Olx Jobs",
  "Internshala",
  "Twenty19",
  "KillerLaunch",
  "Placement Season",
  "HelloIntern",
  "Edugorilla Jobs",
  "RecruitNXT",
  "FirstNaukri",
  "JobBuzz",
  "Peeljobs",
  "MyJobStreet",
  "DreamStarts",
  "BigShyft",
  "iimjobs",
  "Engineeristic",
  "PharmaTutor Jobs",
  "MedJobsIndia",
  "BioJobIndia",
  "CXOJobs",
  "JobSahaayata",
  "WorkIndia.in",
  "QuickJobs",
  "Youth4Work Jobs",
  "Rozgar Bazaar",
  "MahaJobs (Maharashtra Govt.)",
  "TNVelaiPortal (Tamil Nadu Govt.)",
  "Kerala PSC Jobs Portal",
  "MP Rojgar Portal",
  "Haryana Rojgar Portal",
  "UP Rojgar Sangam",
  "SkillConnect Portal (Karnataka Govt.)",
  "Punjab Ghar Ghar Rozgar",
  "Himachal Rojgar Portal",
  "Chhattisgarh Rojgar Portal",
  "Odisha Employment Portal",
  "Assam Career",
  "SarkariExam.com",
  "SarkariJobFind.com",
  "EmploymentNews.gov.in",
  "JobMela.in",
  "Jobhai.com",
  "Rocket.in",
  "GigIndia",
  "FlexingIt",
  "Truelancer",
  "WorkNHire",
  "Freelancer.in",
  "Upwork India",
  "Fiverr India",
  "Others"
]
const statusOptions = ["Call Back","Interested","Ringing Not Reachable", "Not Reachable", "Not Interested", "Not Relevant", "Incorrect Number", "DND" ]

type SortableHeaderProps = {
  column: string
  className?: string
  children: React.ReactNode
}

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#F5F1F9] z-10",
        className
      )}
      // onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {/* {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )} */}
      </div>
    </th>
  );

// Define the User type
type User = {
  id: string
  legalName: string
  brandName: string
  phone: string
  email: string
  ledBy?: string
  source?: string
  status?: string
  callbackDate?: string
  callbackTime?: string
  scheduleDate?: string
  scheduleTime?: string
  recruiter?: {
    id?: string
    email?: string
    username?: string
    [key: string]: any
  }
  [key: string]: any
}



const UserTable: React.FC<{
  users: User[]
  setUsers: React.Dispatch<React.SetStateAction<User[]>>
  onUserSelect: (user: User | null) => void
  expandedRows: string[]
  setExpandedRows: React.Dispatch<React.SetStateAction<string[]>>
  sortBy: string
  sortOrder: "asc" | "desc"
  onSort: (field: string) => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onUserReassign: (user: User) => void
  onSourceChange: (userId: string, newSource: string) => void
  onStatusChange: (userId: string, newStatus: string) => void
  onCallbackSubmit: (userId: string, date: string, time: string) => void
  onScheduleSubmit: (userId: string, date: string, time: string) => void
  setCallbackModalUserId: React.Dispatch<React.SetStateAction<string | null>>
  isLoading: boolean
  onUserEdit?: (user: User) => void
}> = ({
  users,
  setUsers,
  onUserSelect,
  expandedRows,
  setExpandedRows,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onUserReassign,
  onSourceChange,
  onStatusChange,
  onCallbackSubmit,
  onScheduleSubmit,
  setCallbackModalUserId,
  isLoading,
  onUserEdit,
}) => {
  const toggleRow = (id: string) => {
    setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]))
  }

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null
    return sortOrder === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
  }

  const [callbackDate, setCallbackDate] = useState<Date | undefined>()
  const [callbackTime, setCallbackTime] = useState("")
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>()
  const [scheduleTime, setScheduleTime] = useState("")
  const [callbackPopoverOpen, setCallbackPopoverOpen] = useState<string | null>(null)
  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState<string | null>(null)
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null)
  const [callbackModalUserIdState, setCallbackModalUserIdState] = useState<string | null>(null)
  const [scheduleConfirmUserId, setScheduleConfirmUserId] = useState<string | null>(null)
  const [showScheduleUIFor, setShowScheduleUIFor] = useState<string | null>(null)

  const handleCallbackSubmitLocal = (userId: string) => {
    if (callbackDate && callbackTime) {
      onCallbackSubmit(userId, format(callbackDate, "yyyy-MM-dd"), callbackTime)
      setCallbackDate(undefined)
      setCallbackTime("")
    }
  }

  const handleScheduleSubmitLocal = (userId: string) => {
    if (scheduleDate && scheduleTime) {
      onScheduleSubmit(userId, format(scheduleDate, "yyyy-MM-dd"), scheduleTime)
      setScheduleDate(undefined)
      setScheduleTime("")
    }
  }

  return (
    <div className="rounded-lg shadow-sm">
      {/* Desktop Table View */}
      <div className="hidden lg:block min-w-full">
          <table className="min-w-full divide-y divide-border">

        <thead style={{ backgroundColor: '#FFF5F2' }}>
          
            <tr>
              <SortableHeader column="username" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                Name
              </SortableHeader>
              <SortableHeader column="username" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                Legal Name
              </SortableHeader>
              <SortableHeader column="phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
              <SortableHeader column="email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
              <SortableHeader column="ledBy" className="bg-[#FFF5F2] min-w-48">Led By </SortableHeader>
              <SortableHeader column="location" className="bg-[#FFF5F2] min-w-48">Source</SortableHeader>
              <SortableHeader column="createdAt" className="bg-[#FFF5F2] min-w-40">Status</SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                Status Details
              </th>
            </tr>
          </thead>
        
        <tbody>
          {isLoading ? (
            <>
              {[...Array(10)].map((_, index) => (
                <tr key={`shimmer-${index}`} className="bg-white divide-y divide-border">
                  {/* Name */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                  </td>
                  {/* Phone */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                  </td>
                  {/* Email */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-48 h-4"></div>
                  </td>
                  {/* Led By */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-36 h-4"></div>
                  </td>
                  {/* Qualification */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                  </td>
                  {/* Source */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                  </td>
                  {/* Status */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-40 h-10"></div>
                  </td>
                </tr>
              ))}
            </>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={7} className="p-8 text-center text-muted-foreground">
                No users found
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <React.Fragment key={user.id}>
                <tr 
                  className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <td className="p-4 text-gray-800">{user.legalName}</td>
                  <td className="p-4 text-gray-600">{user.brandName || "N/A"}</td>
                  <td className="p-4 text-gray-600">{user.phone}</td>
                  <td className="p-4 text-gray-600">{user.email || "N/A"}</td>
                  <td className="p-4 text-gray-600">{user.recruiter?.email || user.ledBy}</td>
                  <td className="p-4 text-gray-600">{user.source}</td>
               

                  <td className="p-4">
                    <div className="flex flex-col gap-2">
                      <Select value={user.status || ""} onValueChange={(value) => onStatusChange(user.id, value)}>
                        <SelectTrigger className={`w-40 border-gray-300 focus:ring-2 focus:ring-blue-500 text-left ${user.status === "Schedule" ? "pointer-events-none opacity-50 cursor-not-allowed" : ""}`}>
                          <SelectValue placeholder="Select Status" className="text-left" />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((option) => (
                            <SelectItem key={option} value={option.trim()} className={`text-left whitespace-nowrap`}>
                              {option.trim()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {user.status === "Schedule" && showScheduleUIFor === user.id && (
                        <div className="flex flex-col gap-2 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 w-72">
                          <label className="text-xs text-muted-foreground font-medium mb-1">Schedule Date & Time</label>
                          <div className="flex flex-col gap-2">
                            <Popover
                              open={openPopoverId === user.id}
                              onOpenChange={(open) => setOpenPopoverId(open ? user.id : null)}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-gray-300 hover:bg-gray-100 transition-colors w-full justify-start bg-transparent"
                                >
                                  {user.scheduleDate || "Select Date"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 z-50">
                                <Calendar
                                  mode="single"
                                  selected={user.scheduleDate ? new Date(user.scheduleDate) : undefined}
                                  onSelect={(date) => {
                                    setScheduleDate(date)
                                    setOpenPopoverId(null)
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <Input
                              type="time"
                              value={user.scheduleTime || ""}
                              onChange={(e) => setScheduleTime(e.target.value)}
                              className="w-full border-gray-300 focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleScheduleSubmitLocal(user.id)}
                            className="bg-orange-600 hover:bg-orange-700 text-white w-full mt-1"
                          >
                            Submit
                          </Button>
                        </div>
                      )}
                    </div>
                  </td>
                  {/* New Status Details column */}
                 <td className="p-4 text-gray-700">
  {user.status === "Call Back" && user.callbackDate && user.callbackTime ? (
    <span>
      <span className="font-semibold">
        {format(new Date(`${user.callbackDate}T${user.callbackTime}`), "dd MMM yyyy, HH:mm")}
      </span>
    </span>
  ) : (
    <span>{user.status}</span>
  )}
</td>
                </tr>
                {expandedRows.includes(user.id) && (
                  <tr>
                    <td colSpan={7} className="p-4 bg-gray-50 border-b border-gray-200">
                      <div className="text-gray-600">Additional details for {user.name} can be added here.</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
          </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden">
        {isLoading ? (
          <div className="space-y-4 p-4">
            {[...Array(5)].map((_, index) => (
              <div key={`mobile-shimmer-${index}`} className="bg-white rounded-lg border p-4">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  <div className="flex gap-2">
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No users found
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {users.map((user) => (
              <div 
                key={user.id} 
                className="bg-white rounded-lg border p-4 space-y-3"
              >
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-lg text-gray-900 truncate">{user.name}</h3>
                    <p className="text-sm text-gray-600 truncate">{user.phone}</p>
                  </div>
                  <div className="flex-shrink-0 w-full sm:w-auto">
                    <Select value={user.status || ""} onValueChange={(value) => onStatusChange(user.id, value)}>
                      <SelectTrigger className="w-full sm:w-36 border-gray-300 focus:ring-2 focus:ring-blue-500 text-left h-10">
                        <SelectValue placeholder="Status" className="text-left" />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => (
                          <SelectItem key={option} value={option.trim()} className="text-left whitespace-nowrap">
                            {option.trim()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">Email:</span>
                    <span className="truncate text-gray-800">{user.email || "N/A"}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">Led By:</span>
                    <span className="truncate text-gray-800">{user.recruiter?.email || user.ledBy || "N/A"}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">Qualification:</span>
                    <span className="truncate text-gray-800">{user.qualifications || "N/A"}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">Source:</span>
                    <span className="truncate text-gray-800">{user.source || "N/A"}</span>
                  </div>
                </div>

                {/* Status Details */}
                {user.status === "Call Back" && user.callbackDate && user.callbackTime && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-800 mb-1">Callback Scheduled</div>
                    <div className="text-sm text-blue-600 break-words">
                      {format(new Date(`${user.callbackDate}T${user.callbackTime}`), "dd MMM yyyy, HH:mm")}
                    </div>
                  </div>
                )}

                {/* Schedule UI */}
                {user.status === "Schedule" && showScheduleUIFor === user.id && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div className="text-sm font-medium text-gray-700">Schedule Date & Time</div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Date</label>
                        <Popover
                          open={openPopoverId === user.id}
                          onOpenChange={(open) => setOpenPopoverId(open ? user.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-gray-300 hover:bg-gray-100 transition-colors w-full justify-start bg-white h-10"
                            >
                              {user.scheduleDate || "Select Date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-50" align="start">
                            <Calendar
                              mode="single"
                              selected={user.scheduleDate ? new Date(user.scheduleDate) : undefined}
                              onSelect={(date) => {
                                setScheduleDate(date)
                                setOpenPopoverId(null)
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Time</label>
                        <Input
                          type="time"
                          value={user.scheduleTime || ""}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-full border-gray-300 focus:ring-2 focus:ring-blue-500 h-10"
                        />
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleScheduleSubmitLocal(user.id)}
                      className="bg-orange-600 hover:bg-orange-700 text-white w-full h-10"
                    >
                      Submit Schedule
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

  )
}

const ScheduleConfirmModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}> = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xs p-6 relative">
        <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl" onClick={onClose}>
          ×
        </button>
        <h2 className="text-lg font-semibold mb-4 text-center">Are you ready for schedule?</h2>
        <div className="flex justify-center gap-3 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={onConfirm}>
            Yes
          </Button>
        </div>
      </div>
    </div>
  )
}

const CompanyApproachesView: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [filters, setFilters] = useState({
    source: "",
    status: "",
    recruiterId: "",
    fromDate: "",
    toDate: "",
  })
  const {user}=useRbac()
  const [sortBy, setSortBy] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [users, setUsers] = useState<User[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false)
  const [userToReassign, setUserToReassign] = useState<User | null>(null)
  const [userAdded, setUserAdded] = useState(false)
  const [userEdited, setUserEdited] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditApproachModal, setShowEditApproachModal] = useState(false)
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null)
  const [callbackModalUserId, setCallbackModalUserId] = useState<string | null>(null)
  const [previousStatusForCallback, setPreviousStatusForCallback] = useState<string | null>(null)
  const [scheduleConfirmUserId, setScheduleConfirmUserId] = useState<string | null>(null)
  const [showScheduleUIFor, setShowScheduleUIFor] = useState<string | null>(null)
  const [previousStatusForSchedule, setPreviousStatusForSchedule] = useState<string | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
const [recruiters, setRecruiters] = useState<{ id: string; username: string }[]>(
  user !== "ADMIN" 
    ? [{ id: user.id, username: user.username }]
    : []
);
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const [totalUsers,setTotalUsers] = useState(0)
  const navigate = useNavigate() // <-- Add this

  const recruiterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Recruiters" }]
    if (recruiters.length > 0) {
      recruiters.forEach((recruiter) => {
        options.push({
          value: recruiter.id,
          label: recruiter.username,
        })
      })
    } else if (!isLoadingRecruiters) {
      options.push({ value: "no-recruiters", label: "No recruiters available", disabled: true })
    }
    return options
  }, [recruiters, isLoadingRecruiters])

  const sourceOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Sources" }]
    jobPortals.forEach((portal) => {
      options.push({
        value: portal,
        label: portal,
      })
    })
    return options
  }, [])

  const activeFilters = Object.entries(filters).filter(([_, value]) => value !== "")

  // Update getFilterLabel to handle any filter key
  const getFilterLabel = (key: string) => {
    switch (key) {
      case "source":
        return "Source"
      case "status":
        return "Status"
      case "recruiterId":
        return "Recruiter"
      case "fromDate":
        return "From Date"
      case "toDate":
        return "To Date"
      default:
        // Capitalize first letter for unknown keys
        return key.charAt(0).toUpperCase() + key.slice(1)
    }
  }

  const onSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(field)
      setSortOrder("asc")
    }
    setCurrentPage(1)
  }

  const onPageChange = (page: number) => setCurrentPage(page)

  const removeFilter = (key: string) => {
    setFilters((prev) => ({ ...prev, [key]: "" }))
    setCurrentPage(1)
  }

  const clearAllFilters = () => {
    setFilters({
      source: "",
      status: "",
      recruiterId: "",
      fromDate: "",
      toDate: "",
    })
    setSearchTerm("")
    setCurrentPage(1)
  }

  const handleSourceChange = async (userId: string, newSource: string) => {
    const previousSource = users.find(u => u.id === userId)?.source || ""
    setUsers((prevUsers) => prevUsers.map((u) => (u.id === userId ? { ...u, source: newSource } : u)))
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({ ...selectedUser, source: newSource })
    }
    try {
      // Use updateApproach for source changes as updateCompanyApproach doesn't support source
      await updateApproach(userId, { source: newSource })
      // Trigger data refresh after successful source update
      setRefreshTrigger(prev => prev + 1)
    } catch (error) {
      // Revert UI change on error
      setUsers((prevUsers) => prevUsers.map((u) => (u.id === userId ? { ...u, source: previousSource } : u)))
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({ ...selectedUser, source: previousSource })
      }
      console.error("Failed to update source:", error)
      toast.error("Failed to update source. Please try again.")
    }
  }

  const handleStatusChange = async (userId: string, newStatus: string) => {
    const previousUser = users.find(u => u.id === userId)
    const previousStatus = previousUser?.status || ""
    
    if (newStatus === "Call Back") {
      // Store previous status before changing to Call Back
      setPreviousStatusForCallback(previousStatus)
      setCallbackModalUserId(userId)
      // Update UI state but don't call API yet - wait for modal submission
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userId
            ? { ...u, status: newStatus, callbackDate: "", callbackTime: "", scheduleDate: "", scheduleTime: "" }
            : u,
        ),
      )
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({
          ...selectedUser,
          status: newStatus,
          callbackDate: "",
          callbackTime: "",
          scheduleDate: "",
          scheduleTime: "",
        })
      }
      // Don't call API here - wait for modal submission
      return
    }

    // For other statuses, update immediately
    setUsers((prevUsers) =>
      prevUsers.map((u) =>
        u.id === userId
          ? { ...u, status: newStatus, callbackDate: "", callbackTime: "", scheduleDate: "", scheduleTime: "" }
          : u,
      ),
    )
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({
        ...selectedUser,
        status: newStatus,
        callbackDate: "",
        callbackTime: "",
        scheduleDate: "",
        scheduleTime: "",
      })
    }

    try {
      await updateCompanyApproach(userId, { callStatus: newStatus })
      // Trigger data refresh after successful status update
      setRefreshTrigger(prev => prev + 1)
    } catch (error) {
      // Revert UI change on error
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userId ? { ...u, status: previousStatus } : u,
        ),
      )
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({ ...selectedUser, status: previousStatus })
      }
      console.error("Failed to update status:", error)
      toast.error("Failed to update status. Please try again.")
    }
  }

  const handleCallbackSubmit = async (userId: string, date: string, time: string) => {
    try {
      // Update both status and callback time using updateCompanyApproach
      await updateCompanyApproach(userId, {
        callStatus: "Call Back",
        callBackTime: `${date}T${time}`,
      })
      setPreviousStatusForCallback(null)
      // Trigger data refresh
      setRefreshTrigger(prev => prev + 1)
      toast.success("Callback scheduled successfully!")
    } catch (error) {
      // Revert UI change on error
      const previousStatus = previousStatusForCallback || ""
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userId ? { ...u, status: previousStatus, callbackDate: "", callbackTime: "" } : u,
        ),
      )
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({ ...selectedUser, status: previousStatus, callbackDate: "", callbackTime: "" })
      }
      console.error("Failed to update callback:", error)
      toast.error("Failed to update callback. Please try again.")
    }
  }

  const handleScheduleSubmit = (userId: string, date: string, time: string) => {
    setUsers((prevUsers) =>
      prevUsers.map((u) => (u.id === userId ? { ...u, scheduleDate: date, scheduleTime: time } : u)),
    )
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({ ...selectedUser, scheduleDate: date, scheduleTime: time })
    }
  }

  const handleAddTPO = () => {
    setShowAddModal(true)
  }

  const handleAddUser = (user: { legalName: string; brandName: string; number: string; email: string; qualifications: string }) => {
    // Check for duplicates
    const duplicate = users.find(
      (u) =>
        u.phone.trim() === user.number.trim() ||
        u.legalName.trim().toLowerCase() === user.legalName.trim().toLowerCase()
    )
    if (duplicate) {
      // toast.error("User with same email, phone, or name already exists!")
      return false
    }
    setUsers((prev) => [
      ...prev,
      {
        id: (prev.length + 1).toString(),
        legalName: user.legalName,
        brandName: user.brandName,
        phone: user.number,
        email: user.email,
        ledBy: "",
        source: "",
        status: "Ringing",
      },
    ])
    setShowAddModal(false)
    return true
  }

  const handleUserEdit = (user: User) => {
    setSelectedUserForEdit(user)
    setShowEditApproachModal(true)
  }

  const handleReassignUser = (user: User) => {
    setUserToReassign(user)
    setIsReassignModalOpen(true)
  }

  const handleReassignSuccess = () => {
    setIsReassignModalOpen(false)
    setUserToReassign(null)
  }

  const handleUserUpdate = (updatedUser: User) => {
    setUsers((prevUsers) => prevUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)))
    setSelectedUser(updatedUser)
  }


  const fetchRecruiters = useCallback(async () => {
    setIsLoadingRecruiters(true)
    try {
      const response = await getMySubordinates()
      if (response?.data?.subordinates) {
        setRecruiters((prev) => [...prev, ...response.data.subordinates])
      } else {
        setRecruiters([])
      }
    } catch (err: any) {
      console.error("Failed to fetch recruiters:", err)
      setRecruiters([])
    } finally {
      setIsLoadingRecruiters(false)
    }
  }, [])

  useEffect(() => {
    fetchRecruiters()
  }, [fetchRecruiters])

  // Export to Excel function
  const handleExportExcel = async () => {
    setIsExportingExcel(true)
    try {
      toast.loading("Exporting data to Excel...", { id: "export-excel" })
      
      let allApproaches: any[] = []
      let currentPage = 1
      let totalPages = 1
      const pageLimit = 10 // Fetch 20 records per page

      // Fetch all pages of data
        const params: any = {
          search: searchTerm || "",
          source: filters.source || "",
          callStatus: filters.status || "",
          recruiterId: filters.recruiterId || "",
          fromDate: filters.fromDate || "",
          toDate: filters.toDate || "",
          sortBy,
          sortOrder,
        }

        const data = await exportCompanyApproaches(params)
        console.log("data:", JSON.stringify(data,null,2))
        allApproaches = data || []
      
      if (allApproaches.length === 0) {
        toast.error("No data to export", { id: "export-excel" })
        return
      }

      // Map data to Excel format
      const excelData = allApproaches.map((u: any) => {
        let callbackDate = "N/A"
        let callbackTime = "N/A"
        if (u.callBackTime) {
          const dt = new Date(u.callBackTime)
          callbackDate = dt.toLocaleDateString('en-CA').slice(0, 10)
          callbackTime = dt.toTimeString().slice(0, 5)
        }
        return {
          "Legal Name": u.legalName || "N/A",
          "Brand Name": u.brandName || "N/A",
          "Phone": u.phone || "N/A",
          "Email": u.email || "N/A",
          "Source": u.source || "N/A",
          "Status": u.callStatus || "N/A",
          "Callback Date": callbackDate,
          "Callback Time": callbackTime,
          "Led by": u.recruiter?.email || "N/A",

        }
      })

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(excelData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Approaches")

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
      const blob = new Blob([excelBuffer], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `approaches_${format(new Date(), 'yyyy-MM-dd')}.xlsx`)
      document.body.appendChild(link)
      link.click()

      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success("Excel file downloaded successfully!", { id: "export-excel" })
    } catch (error: any) {
      console.error("Failed to export Excel:", error)
      toast.error(`Failed to export: ${error?.response?.data?.message || error?.message || 'Unknown error'}`, { 
        id: "export-excel" 
      })
    } finally {
      setIsExportingExcel(false)
    }
  }

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const params: any = {
          search: searchTerm || "",
          source: filters.source || "",
          callStatus: filters.status || "",
          recruiterId: filters.recruiterId || "",
          fromDate: filters.fromDate || "",
          toDate: filters.toDate || "",
          page: currentPage,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
        }
        const data = await getCompanyApproaches(params)
        // Map backend fields to frontend User shape
        console.log("Fetched approaches data:", data)
        const mappedUsers = Array.isArray(data.data)
          ? data.data.map((u: any) => {
              let callbackDate = ""
              let callbackTime = ""
              if (u.callBackTime) {
                const dt = new Date(u.callBackTime)
                callbackDate = dt.toLocaleDateString('en-CA').slice(0, 10) // yyyy-mm-dd
                callbackTime = dt.toTimeString().slice(0, 5) // HH:mm
              }
              return {
                id: u._id,
                    legalName: u.legalName,
                    brandName: u.brandName,
                phone: u.phone,
                email: u.email,
                ledBy: u.recruiter?.email || "",
                source: u.source,
                status: u.callStatus,
                callbackDate,
                callbackTime,
                scheduleDate: u.scheduleDate || "",
                scheduleTime: u.scheduleTime || "",
                jobId: u.jobId,
                recruiter: u.recruiter,
              }
            })
          : []
        setUsers(mappedUsers)
        setTotalUsers(data.totalResults || 0)
        setTotalPages(data.totalPages || 1)
      } catch (error) {
        setUsers([])
        setTotalPages(1)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [searchTerm, filters, currentPage, itemsPerPage, sortBy, sortOrder, showAddModal, showEditApproachModal, refreshTrigger])

  return (
    <div data-page-scroll="internal" className="bg-background flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-background border-b border-border z-40">
        <div className="px-3 sm:px-6 py-4">
          <div className="space-y-4">
            {/* Search Row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by names, number, email, led by, qualifications"
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchTerm(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="pl-10 w-full"
                />
              </div>
              <Button variant="default" size="sm" onClick={handleAddTPO} className="bg-[#EC6E51] hover:bg-[#d65f47] flex-shrink-0">
                Add
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={isExportingExcel}
                className="hover:bg-gray-200 flex-shrink-0 flex items-center gap-2"
              >
                {isExportingExcel ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export
                  </>
                )}
              </Button>
            </div>
            
            {/* Filters Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              <SearchableSelect
                options={sourceOptions}
                value={filters.source || "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({ ...prev, source: value === "all" ? "" : value }))
                  setCurrentPage(1)
                }}
                placeholder="All Sources"
                emptyText="No sources found"
                searchPlaceholder="Search source..."
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                searchable
              />
              <Select
                value={filters.status || "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({ ...prev, status: value === "all" ? "" : value }))
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {user?.role !== "BDE_INTERN" && <SearchableSelect
                options={recruiterOptions}
                value={filters.recruiterId || "all"}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, recruiterId: v === "all" ? "" : v }))
                  setCurrentPage(1)
                }}
                placeholder={isLoadingRecruiters ? "Loading..." : "All Recruiters"}
                emptyText="No recruiters found"
                searchPlaceholder="Search by username..."
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                loading={isLoadingRecruiters}
                icon={<UserIcon className="h-4 w-4 text-gray-500" />}
                searchable
              />}
              <Input
                type="date"
                value={filters.fromDate}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
                  setCurrentPage(1)
                }}
                className="w-full"
                placeholder="From Date"
              />
              <Input
                type="date"
                value={filters.toDate}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, toDate: e.target.value }))
                  setCurrentPage(1)
                }}
                className="w-full"
                placeholder="To Date"
              />
            </div>
            {(activeFilters.length > 0 || searchTerm) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchTerm && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Search: {searchTerm}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => {
                        setSearchTerm("")
                        setCurrentPage(1)
                      }}
                    />
                  </Badge>
                )}
                {Object.entries(filters)
                  .filter(([_, value]) => value !== "")
                  .map(([key, value]) => (
                    <Badge key={key} variant="secondary" className="flex items-center gap-1">
                      {getFilterLabel(key)}:{" "}
                      {key === "recruiterId" ? recruiters.find((r) => r.id === value)?.username || value : value}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeFilter(key)} />
                    </Badge>
                  ))}
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs">
                  Clear all
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <div className={`transition-all duration-300 ${selectedUser ? "mr-96" : ""}`}>
          <UserTable
              users={users}
              setUsers={setUsers}
              onUserSelect={setSelectedUser}
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onUserReassign={handleReassignUser}
              onSourceChange={handleSourceChange}
              onStatusChange={handleStatusChange}
              onCallbackSubmit={handleCallbackSubmit}
              onScheduleSubmit={handleScheduleSubmit}
              setCallbackModalUserId={setCallbackModalUserId}
              isLoading={isLoading}
              onUserEdit={handleUserEdit}
            />
          </div>
      </div>
        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 flex-shrink-0 bg-white border-t border-gray-200">
        <div className="text-sm text-gray-500 text-center sm:text-left">
          Showing {users.length} of {totalUsers} candidates
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || isLoading}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          {/* Desktop: Show page numbers */}
          <div className="hidden sm:flex items-center gap-2">
            {(() => {
              const pageNumbers = []
              const maxPagesToShow = 6
              let startPage = Math.max(1, currentPage - 2)
              const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

              if (endPage - startPage + 1 < maxPagesToShow) {
                startPage = Math.max(1, endPage - maxPagesToShow + 1)
              }

              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i)
              }

              return (
                <>
                  {pageNumbers.map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      disabled={isLoading}
                      className={currentPage === page ? "bg-[#EC6E51] text-primary-foreground" : ""}
                    >
                      {page}
                    </Button>
                  ))}
                  {endPage < totalPages && (
                    <>
                      <span className="text-sm text-muted-foreground">...</span>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={isLoading}>
                        {totalPages}
                      </Button>
                    </>
                  )}
                </>
              )
            })()}
          </div>
          
          {/* Mobile: Show page number */}
          <div className="sm:hidden">
            <span className="text-sm text-gray-600 px-2">
              {currentPage} / {totalPages}
            </span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages || isLoading}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

     <AddCompanyModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onAdd={handleAddUser} />
      <CallbackModal
        isOpen={!!callbackModalUserId}
        onClose={() => {
          // Revert status to previous when cancelled
          if (callbackModalUserId && previousStatusForCallback) {
            setUsers((prevUsers) =>
              prevUsers.map((u) =>
                u.id === callbackModalUserId
                  ? { ...u, status: previousStatusForCallback }
                  : u,
              ),
            )
            if (selectedUser && selectedUser.id === callbackModalUserId) {
              setSelectedUser({
                ...selectedUser,
                status: previousStatusForCallback,
              })
            }
          }
          setCallbackModalUserId(null)
          setPreviousStatusForCallback(null)
        }}
        onSubmit={(date, time) => {
          if (callbackModalUserId) {
            handleCallbackSubmit(callbackModalUserId, date, time)
            setCallbackModalUserId(null)
          }
        }}
        initialDate={users.find((u) => u.id === callbackModalUserId)?.callbackDate}
        initialTime={users.find((u) => u.id === callbackModalUserId)?.callbackTime}
      />
      <ScheduleConfirmModal
        isOpen={!!scheduleConfirmUserId}
        onClose={() => {
          // Revert status to previous when cancelled
          if (scheduleConfirmUserId && previousStatusForSchedule) {
            setUsers((prevUsers) =>
              prevUsers.map((u) =>
                u.id === scheduleConfirmUserId
                  ? { ...u, status: previousStatusForSchedule }
                  : u,
              ),
            )
            if (selectedUser && selectedUser.id === scheduleConfirmUserId) {
              setSelectedUser({
                ...selectedUser,
                status: previousStatusForSchedule,
              })
            }
          }
          setScheduleConfirmUserId(null)
          setPreviousStatusForSchedule(null)
        }}
        onConfirm={() => {
          const scheduledUser = users.find(u => u.id === scheduleConfirmUserId)
          console.log('Navigating with scheduledUser:', scheduledUser, 'jobId:', scheduledUser?.jobId)
          setShowScheduleUIFor(scheduleConfirmUserId)
          setScheduleConfirmUserId(null)
          setPreviousStatusForSchedule(null)
          navigate("/add-candidate", {
            state: {
              fromApproaches: true,
              approachPayload: {
                name: scheduledUser?.name || '',
                phone: scheduledUser?.phone || '',
                email: scheduledUser?.email || '',
                qualifications: scheduledUser?.qualifications || '',
                source: scheduledUser?.source || '',
                callStatus: "Schedule",
                remarks: scheduledUser?.remarks || '',
                jobId: scheduledUser?.jobId || '',
              },
              prefill: {
                dateOfBirth: scheduledUser?.dob || scheduledUser?.dateOfBirth || '',
                skills: Array.isArray(scheduledUser?.skills) ? scheduledUser?.skills : [],
                workExperience: scheduledUser?.workExperience || 0,
              },
            },
          })
        }}
      />
      <AddApproachModal
        isOpen={showEditApproachModal}
        onClose={() => {
          setShowEditApproachModal(false)
          setSelectedUserForEdit(null)
        }}
        candidate={selectedUserForEdit ? {
          id: selectedUserForEdit.id,
          name: selectedUserForEdit.name,
          email: selectedUserForEdit.email,
          phone: selectedUserForEdit.phone,
          highestQualification: selectedUserForEdit.qualifications,
          profileSource: selectedUserForEdit.source,

        } : null}
      />
    </div>
  )
}

export default CompanyApproachesView